import {
    dateObjectToString, dateTimeObjectToString, roundDecimals,
    StringToDateObject,
    StringToDateObjectAddTime,
    StringToDateTimeObject
} from '../../fromKotlin/nkutils';
import JSDB from '../../fromKotlin/nk';
import IItem from '../product_delivery';
import {addHours, parseJSON} from 'date-fns';
import {displayDocumentId, DocumentStatus, DocumentType, ExtendedDocument, isDocumentPrinted, ProductNote} from './Document';
import value from '../../assets/scss/_themes-vars.module.scss';
import {CartesetDataHolder} from '../CartesetDataHolder';
import {distinct} from '../../utils/sort';
import {HandLine} from "./TaxNote";


export const documentTypes = {
    invoice: {
        name: 'חשבונית מס',
        color: value.primaryLight,
        textColor: value.primary800,
        baseColor: 'error',
        nkType: 0
    },
    tax_invoice: {
        name: 'חשבונית מס קבלה',
        color: value.secondaryLight,
        textColor: value.secondaryDark,
        baseColor: 'secondary',
        nkType: 1
    },
    refund: {
        name: 'חשבונית זיכוי',
        textColor: value.grey900,
        color: value.warningMain,
        baseColor: 'warning',
        nkType: 2
    },
    delivery_certificate: {
        name: 'תעודת משלוח',
        color: value.successLight,
        textColor: value.successDark,
        baseColor: 'success',
        nkType: 4
    },
    supplier_note: {
        name: 'תעודת ספק',
        color: value.successLight,
        textColor: value.successDark,
        baseColor: 'success',
        nkType: 6
    },
    visit_note: {
        name: 'תעודת ביקור',
        color: value.successLight,
        textColor: value.successDark,
        baseColor: 'success',
        nkType: 12
    },
    all_tax: {
        name: 'מחזור',
        color: value.successLight,
        textColor: value.successDark,
        baseColor: 'success',
        nkType: 5
    },
    order: {name: 'הזמנה', color: value.grey100, textColor: value.orangeDark, baseColor: 'blue', nkType: 10},
    receipt: {name: 'קבלה', color: value.grey200, textColor: value.grey600, baseColor: 'gray', nkType: 7},
    debt: {name: 'חוב קודם', color: value.grey200, textColor: value.grey600, baseColor: 'gray', nkType: 9},
    sup_invoice: {
        name: 'חשבונית ספק',
        color: value.primaryLight,
        textColor: value.primary800,
        baseColor: 'error',
        nkType: 16
    },
    sup_tax_invoice: {
        name: 'חשבונית מס ספק/קבלה',
        color: value.secondaryLight,
        textColor: value.secondaryDark,
        baseColor: 'secondary',
        nkType: 17
    },
    sup_refund: {
        name: 'חשבונית זיכוי ספק',
        textColor: value.grey900,
        color: value.warningMain,
        baseColor: 'warning',
        nkType: 18
    },
    sup_receipt: {name: 'תשלום לספק', color: value.grey200, textColor: value.grey600, baseColor: 'gray', nkType: 19},
    sup_debt: {name: 'חוב ספק קודם', color: value.grey200, textColor: value.grey600, baseColor: 'gray', nkType: 20},
};

export interface Check {
    id?: number;
    bank?: number | string;
    branch?: number;
    account?: number;
    dueDate?: Date;
    percent?: number;
    baseAmount?: number;
}

export type PaymentMethod = 'cash' | 'credit' | 'check' | 'deposit' | 'bit' | 'withholding_tax' | 'barter' |'fastCheck';
export const paymentMethods = {
    cash: {name: 'מזומן'},
    credit: {name: 'אשראי'},
    check: {name: 'צ\'ק'},
    deposit: {name: 'העברה בנקאית'},
    bit: {name: 'ביט'},
    withholding_tax: {name: 'ניכוי מס במקור'},
    barter: {name: 'ברטר'},
    fastCheck: {name: 'צ\'ק מהיר'},
};

const BANK_PLACEHOLDER = 'בחר בנק';
const PAY_METHODS_WITH_BANK: PaymentMethod[] = ['check', 'deposit', 'fastCheck'];

const bankOptionCode = (option: string): number => {
    const m = String(option).match(/-(-?\d+)$/);
    return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
};

/** Bank dropdown options with "בחר בנק" first so Latin names like GROW are not the default. */
export const getBankSelectOptions = (): string[] => {
    const raw = (JSDB().getPaymentMethods()?.toArray?.() ?? []) as string[];
    return [...raw].sort((a, b) => {
        const ac = bankOptionCode(a);
        const bc = bankOptionCode(b);
        const aPh = ac === -1 || String(a).includes(BANK_PLACEHOLDER);
        const bPh = bc === -1 || String(b).includes(BANK_PLACEHOLDER);
        if (aPh !== bPh) return aPh ? -1 : 1;
        if (ac !== bc) return ac - bc;
        return String(a).localeCompare(String(b), 'he');
    });
};

/** Map stored bank id/name onto a dropdown option; empty/0 is unselected (not GROW). */
export const matchBankOption = (bank: string | number | undefined | null, options?: string[]): string | null => {
    if (bank === undefined || bank === null || bank === '' || bank === 0 || bank === '0') return null;
    const opts = options ?? getBankSelectOptions();
    const asStr = String(bank);
    const exact = opts.find((o) => o === asStr);
    if (exact) return exact;
    const asNum = Number(asStr);
    if (asStr.trim() !== '' && !Number.isNaN(asNum)) {
        const byCode = opts.find((o) => bankOptionCode(o) === asNum);
        if (byCode) return byCode;
    }
    return opts.find((o) => o.startsWith(`${asStr}-`)) ?? null;
};

export interface Payment {
    id: string;
    amount: number;
    method: PaymentMethod;
    check?: Check;
    paid?: number;
    paid_date?: string;
    paid_details?: string;
    notes?: string;
    category?: string;
    canceled?:number
    /** Stable PaymentData.inner_id when persisted; undefined/0 = legacy (use id as index). */
    inner_id?: number;
}

/** Empty / placeholder cheque fields — should still be filled from last-cheque memory. */
export const isBlankCheckMemoryField = (v: unknown): boolean => {
    if (v === undefined || v === null || v === '' || v === 0 || v === '0') return true;
    return String(v).includes(BANK_PLACEHOLDER);
};

/** Bank / account / branch from the client's last cheque, without copying cheque number. */
export const rememberedCheckFields = (memory?: Check, current?: Check): Check => ({
    dueDate: current?.dueDate ?? new Date(),
    bank: isBlankCheckMemoryField(current?.bank)
        ? (matchBankOption(memory?.bank) ?? memory?.bank)
        : current?.bank,
    account: isBlankCheckMemoryField(current?.account) ? memory?.account : current?.account,
    branch: isBlankCheckMemoryField(current?.branch) ? memory?.branch : current?.branch,
    id: current?.id,
    percent: current?.percent,
    baseAmount: current?.baseAmount,
});

/** Fill blank cheque bank/account/branch from last-cheque memory. */
export const applyCheckMemory = (payment: Payment, memory?: Check): Payment => {
    if (!memory) return payment;
    return {...payment, check: rememberedCheckFields(memory, payment.check)};
};

const paymentBankLabel = (p: any): string | number | undefined => {
    try {
        if (typeof p?.bankName === 'function') {
            const name = p.bankName();
            if (name) return name;
        } else if (p?.bankName) {
            return p.bankName;
        }
    } catch {
        /* bank_id is not numeric — fall back to the stored code */
    }
    return p?.bank_id;
};

const paymentCheckFromKotlin = (p: any, default_date: any): Check => {
    const date = (StringToDateObject(JSDB().getDatesManipulator().dateIsrael(p.check_date)));
    return {
        id: p.check_number,
        bank: paymentBankLabel(p),
        branch: p.bank_branch,
        account: p.bank_number,
        dueDate: isNaN(date.getDate()) ? default_date : date
    };
};

export const serializePayment = (p, i,default_date=undefined): Payment => {
    let method: PaymentMethod = 'cash';
    let check;
    const methodName = String(p?.payMethod?.name ?? p?.payMethod ?? '');
    const methodNum = Number(p?.pay_method);
    if (methodName === 'CASH' || methodNum === 1) {
        method = 'cash';
    } else if (methodName === 'CHECK' || methodNum === 2) {
        method = 'check';
        check = paymentCheckFromKotlin(p, default_date);

    } else if (methodName === 'CARD' || methodNum === 3) {
        method = 'credit';
    } else if (methodName === 'BANK' || methodNum === 4) {
        check = paymentCheckFromKotlin(p, default_date);
        method = 'deposit';
    } else if (methodName === 'BIT' || methodNum === 5) {
        method = 'bit';
    } else if (methodName === 'MASS_BAMACOR' || methodNum === 6) {
        method = 'withholding_tax';
    } else if (methodName === 'BARTER' || methodNum === 7) {
        method = 'barter';
    } else if (methodName === 'FAST_CHECK' || methodNum === 8) {
        method = 'fastCheck';
        check = paymentCheckFromKotlin(p, default_date);
    }

    const innerId = Number(p?.inner_id) || 0;
    return {
        amount: p.value,
        check,
        id: i ?? '',
        method,
        notes: p?.notes,
        category: p?.category,
        paid: p?.paid,
        paid_date: p?.paid_date,
        paid_details: p?.paid_details,
        canceled: p?.canceled ?? 0,
        inner_id: innerId > 0 ? innerId : undefined,

    };
};
// ---------------------------------------------------------------------------
// Stage 3 JSON boundary: each document header is serialized ONCE on the Kotlin
// side (JSDB().documentToJson) and the deserializers below read plain JSON
// instead of reaching into live Kotlin members. nkObject is still attached to
// the resulting document for the rest of the app (products, editors, previews)
// but utils.ts itself no longer depends on Kotlin member-name survival.
// ---------------------------------------------------------------------------
export const documentHeader = (n: any): any => JSON.parse(JSDB().documentToJson(n));

/** Resolved driver display name via Kotlin getDriver() (order forced driver / linked order / API driver_id). */
export function resolveDocumentDriverName(nkObject: any, header?: any): string | undefined {
    if (header?.driverName) return header.driverName;
    const fromNote = nkObject?.getDriver?.()?.user_name;
    if (fromNote) return fromNote;
    const did = Number(nkObject?.driver_id);
    if (did > 0) {
        return JSDB().getAgentBy(did, null, false, true).first?.user_name;
    }
    return undefined;
}

export function resolveDocumentCollectorName(nkObject: any, header?: any): string | undefined {
    if (header?.collectorName) return header.collectorName;
    const fromNote = nkObject?.getCollector?.()?.user_name;
    if (fromNote) return fromNote;
    const cid = Number(nkObject?.collector_id);
    if (cid > 0) {
        return JSDB().getAgentBy(cid, null, false, false, true).first?.user_name;
    }
    return undefined;
}

// JSON payments carry bankName as a value; reuse serializePayment's logic by
// shimming it back to the callable the raw-Kotlin path expects.
const paymentFromJson = (p: any, i: number, default_date: any = undefined): Payment =>
    serializePayment({ ...p, bankName: () => p.bankName }, i, default_date);

const detailsFromJson = (d: any): Details => ({
    streetNumber: d.streetNumber,
    city: d.city,
    companyId: d.business_id,
    companyName: d.business_name,
    street: d.street,
    id: d.id,
    phone: d.phone,
    email: '',
    supplierInputEquipment: !!d.supplier_input_equipment,
});

const statusFromJson = (j: any): DocumentStatus => {
    let status: DocumentStatus;
    if (!j.used && j.active) {
        status = 'open';
    } else if (!j.active) {
        status = 'canceled';
    } else if (j.active && j.used) {
        status = 'closed';
    } else {
        status = 'approved';
    }
    return status;
};

export const deserializePaymentMethod = (m): number => {
    let method = 0;
    if (m === 'cash') {
        method = 1;
    } else if (m === 'check') {
        method = 2;
    } else if (m === 'credit') {
        method = 3;
    } else if (m === 'deposit') {
        method = 4;
    } else if (m === 'bit') {
        method = 5;
    } else if (m === 'withholding_tax') {
        method = 6;
    } else if (m === 'barter') {
        method = 7;
    } else if (m === 'fastCheck') {
        method = 8;
    }
    return method
}
/** "NAME-CODE" (or already-numeric id) for createPayNoteDataFromData. */
export const bankLabelForBackend = (bank: string | number | undefined | null): string => {
    if (isBlankCheckMemoryField(bank)) return '';
    return matchBankOption(bank) ?? String(bank);
};

export const deserializePayment = (payment: Payment): any => {
    let method = deserializePaymentMethod(payment.method);
    const usesBank = PAY_METHODS_WITH_BANK.includes(payment.method);


    return JSDB().createPayNoteDataFromData(
        Number(payment.amount),
        method,
        usesBank ? bankLabelForBackend(payment.check?.bank) : '',
        usesBank && payment.check?.branch ? payment.check?.branch : '',
        usesBank && payment.check?.account ? payment.check?.account : '',
        payment.check?.id ? payment.check?.id : '',
        payment.check?.dueDate ? JSDB().getDatesManipulator().dateIsrael(dateObjectToString(payment.check?.dueDate)) : '',
        payment.notes,
        payment.category,
        payment.canceled,
        payment.paid == null ? null : Number(payment.paid),
        payment.paid_date ?? null,
        payment.paid_details ?? null,
        Number(payment.inner_id) || 0,
    );
};

/** Drop zero-value payment lines before they are converted to PaymentData and sent. */
export const filterZeroPayments = (payments: Payment[] | undefined | null): Payment[] =>
    (payments ?? []).filter((p) => Number(p.amount) !== 0);

export const deserializePaymentsForBackend = (payments: Payment[] | undefined | null): any[] =>
    filterZeroPayments(payments).map(deserializePayment);


export interface Details {
    id: number;
    companyName: string;
    companyId: string;
    city: string;
    street: string;
    streetNumber: string,
    phone: string;
    email: string
    supplierInputEquipment?: boolean
}

const parseStaticAddress = (address?: string) => {
    if (!address) return { city: '', street: '', streetNumber: '' };
    const parts = String(address).split(',');
    return {
        city: parts[0]?.trim() ?? '',
        street: parts[1]?.trim() ?? '',
        streetNumber: parts[2]?.trim() ?? '',
    };
};

const emptyDetails = (): Details => ({
    id: 0,
    companyName: '',
    companyId: '',
    city: '',
    street: '',
    streetNumber: '',
    phone: '',
    email: '',
});

/** Read static client fields from a Kotlin note, ClientStaticData, or Details-shaped object. */
export const serializeDetails = (doc): Details => {
    const sd = (typeof doc?.getStaticData === 'function' ? doc.getStaticData() : null)
        ?? doc?.ClientStaticData
        ?? doc;
    if (!sd) return emptyDetails();

    const fromAddress = parseStaticAddress(sd.address);
    return {
        id: sd.id ?? 0,
        companyName: sd.business_name ?? '',
        companyId: sd.business_id ?? '',
        phone: sd.phone ?? '',
        city: sd.city ?? fromAddress.city,
        street: sd.street ?? fromAddress.street,
        streetNumber: sd.streetNumber ?? fromAddress.streetNumber,
        email: sd.email ?? '',
        supplierInputEquipment: !!(sd.supplier_input_equipment ?? sd.supplierInputEquipment),
    };
};
export type visitStates = { label: "ביקור כללי", value: 0 }
    | { label: "ביצעתי הזמנה", value: 1 }
    | { label: "ביצעתי מכירה", value: 2 }
    | { label: "לא צריך סחורה", value: 3 }
    | { label: "לקחתי החזרות", value: 4 }
    | { label: "לקחתי תשלום", value: 5 }
    | { label: "הוספתי לקוח", value: 6 }
    | { label: "יש מתקן בנקודה", value: 7 }
    | { label: "האם הנקודה מוגדרת", value: 8 };

export const visitStatesMap = {
    "ביקור כללי": 0,
    "ביצעתי הזמנה": 1,
    "ביצעתי מכירה": 2,
    "לא צריך סחורה": 3,
    "לקחתי החזרות": 4,
    "לקחתי תשלום": 5,
    "הוספתי לקוח": 6,
    "יש מתקן בנקודה": 7,
    "האם הנקודה מוגדרת": 8


}
export const deserializeDetails = (details: Details): any => {
    const fromAddress = parseStaticAddress((details as Details & { address?: string }).address);
    const city = details.city || fromAddress.city;
    const street = details.street || fromAddress.street;
    const streetNumber = details.streetNumber || fromAddress.streetNumber;
    return JSDB().createClientStaticData(
        Number(details.id),
        details.companyName ?? '',
        details.companyId ?? '',
        details.phone ?? '',
        city,
        street,
        streetNumber,
    );
};

export function applySupplierInputEquipment(staticData: any, equipment?: boolean) {
    if (!staticData || !equipment) return staticData;
    staticData.supplier_input_equipment = true;
    return staticData;
}

/**
 * Nothing in the first unit, only a second unit without conversion or an order-only third unit.
 * Neither is charged, so issuing a delivery note or invoice drops the line.
 */
export const needsTypedFirstUnit = (p: any): boolean => {
    if (Number(p?.quantity) > 0 || Number(p?.quantityReturns) > 0) return false;
    const second = Number(p?.quantitySecond) || 0;
    if (second > 0 && Number(p?.conversion_ratio) > 0) return false;
    return second > 0 || (Number(p?.quantityThird) || 0) > 0;
};

export const TYPED_FIRST_UNIT_MESSAGE = 'יש מוצר בלי כמות ביחידה הראשונה (יחידה משנית בלי המרה או יחידה שלישית). הקלד את הכמות ביחידה הראשונה.';

export function itemApiIInternal(p, date, client,): IItem {
    const product = p.getProduct();
    const op = product.getOrderProduct();
    // Supplier products: their own units, or the linked client product's.
    const supplierProduct = product.isClient === false;
    let barcode = '';
    if (product.getBarcode() != '0000000000000')
        barcode = product.getBarcode();
    let unit1 = supplierProduct ? product.getEffectiveUnitName() : product.getUnitName();
    const units: string[] = [];
    if (unit1 == '') {
        unit1 = 'יח\'';
    }
    units.push(unit1);
    let hasSecondUnitName = false;
    if (op != null || supplierProduct) {
        const unit2 = supplierProduct ? product.getEffectiveExtraUnitName() : op.getExtraUnitName();
        if (unit2 != null && unit2 != '') {
            units.push(unit2);
            hasSecondUnitName = true;
        }
        if (JSDB().userCofcConf?.third_unit_on == 1) {
            const unit3 = supplierProduct ? product.getEffectiveThirdUnitName() : op.getThirdUnitName();
            if (unit3 != null && unit3 != '') {
                units.push(unit3);
            }
        }
    }
    // A line stored with its own second unit (wu) — e.g. an AI-read supplier
    // document — still shows that amount when the product has no second unit.
    if (!hasSecondUnitName && (p.wrapped_amount > 0 || p.wrapped_amount_return > 0) && p.wrapped_unit > 0) {
        units.splice(1, 0, String(p.getUnit()));
        hasSecondUnitName = true;
    }

    const price = p.price;
    const discount = p.discount;
    const cost = JSDB().getProductCost(product.id)?.get(date)
    let totalCost = (cost?.first ?? 0) * (1 - (cost?.second ?? 0) / 100)

    return {
        discount,
        nkObject: p,
        quantityReturns: p.returns,
        quantitySecondReturns: p.wrapped_amount_return,
        totalPrice: price,
        id: product.id,
        external_id: product.getExternalId(),
        name: product.getName(),
        image: '',
        barcode,
        costPrice: totalCost,
        fixedCostPrice: product.getCostPrice(),
        position: product.getPosition(),
        quantity: p.value,
        quantitySecond: p.wrapped_amount,
        quantityThird: p.third_amount,
        no_tax_product: product.getNoTaxProduct() === 1,
        unit: unit1,
        in_unit: product.getUnitAmmount(date),
        category: product.getCategory(),
        category2: product.getCategory2(),
        available_units: units,
        hasSecondUnitName,
        notes: p.notes,
        notes2: p.notes2,
        volume:op?.volume,
        notes3: p.notes3,
        isLocked:p.o_locked==1,
        conversion_ratio: p.conversion_ratio,
        totalPriceInclude:p.priceAfter,
        totalPriceExclude: p.priceBefore,
        notePlacement: JSDB().getUser().first.print_state == 200 ? p.tempPosition : undefined

    };
}

export function itemsApi(client: any, date: string, pds: any): IItem[] {
    const items: IItem[] = [];
    if (pds.size > 0)
        pds.toArray().forEach((p: any) => {

            items.push(itemApiIInternal(p, date, client));

        });


    return items;


}


export const createDocument = ({
                                   id = 0,
                                   entId = '',
                                   entName = '',
                                   date = new Date(),
                                   issueDate = new Date(),
                                   type = 'invoice',
                                   allDay = false,
                                   status = 'pending_approval',

                                   payments = [],
                                   receiptsIds = [],
    receiptsDisplayIds,
                                   deliveryCertificatesIds = [],
                                   products = [],
                                   agent = '',

                                   mainValue = 0,
                                   preValue = undefined,
                                   taxValue = undefined,
                                   discount = undefined,
                                   round = undefined,
                                   round_tax = undefined,
                                   hand = undefined,
                                   hand_tax = undefined,
                                   hand_text = undefined,
                                   details,
                                   comment = '',
                                   bul = undefined,
                                   category = undefined,
                                   orderStatus = undefined,
    sign = undefined,
    photo = undefined,
    photoCount = undefined,
                                   refundRest = undefined,
                                   entBName = '',
                                   handArray = undefined,
                                    notes2= undefined,
                                    notes3= undefined,
    notes4=undefined,
    notes5=undefined,
    notes6=undefined,
    notes7=undefined,
    notes8=undefined,
    notes9=undefined,
    notes10=undefined,
    agent_id=undefined,
    delivery_date = undefined,
    staticData = undefined,

    history,
                                   taxConfirmation = undefined,
                                   supplierInvoiceNumber = undefined,
                                   supplierInputEquipment = undefined,
                                   nkObject = null,
                                   ref_id: refIdParam = undefined,
                                   header = undefined,
                               }: {
    id?: number;
    entId?: string;
    entName?: string;
    date?: Date;
    issueDate?: Date;
    type?: DocumentType;
    allDay?: boolean;
    status?: DocumentStatus;
    payments?: Payment[];
    receiptsIds?: number[];
    receiptsDisplayIds?: string[];
    deliveryCertificatesIds?: number[];
    products?: IItem[];
    agent?: string;
    discount?: number;
    details?: Details;
    comment: string;
    mainValue: number
    taxValue?: number
    preValue?: number
    round?: number
    round_tax?: number
    hand?: number
    hand_tax?: number
    hand_text?: string
    bul?: number
    category?: string
    orderStatus?: string
    handArray?: HandLine[]
    sign?: boolean
    photo?: boolean
    photoCount?: number
    entBName?: string
    refundRest?: boolean
    notes2?: string
    notes3?: string
    notes4?: string
    notes5?: string
    notes6?: string
    notes7?: string
    notes8?: string
    notes9?: string
    notes10?: string
    agent_id?: number
    delivery_date?: Date
    staticData?: Details
    history?: any
    taxConfirmation?:string
    supplierInvoiceNumber?: string
    supplierInputEquipment?: boolean
    nkObject?: any;
    ref_id?: string;
    // Parsed JSON from JSDB().documentToJson — when present, used instead of
    // digging into the live Kotlin nkObject.
    header?: any;
}): ExtendedDocument => {
    let doc;
    if (type === 'delivery_certificate' || type === 'supplier_note') {
        doc = {
            products,
            taxValue,
            preValue,
            bul,
            comment,
            staticData,
            returnsOnly: header ? header.onlyReturns : nkObject?.isOnlyReturns(),
        };

    } else if (type === 'tax_invoice' || type === 'refund' || type === 'invoice' || type==='sup_invoice' || type==='sup_refund' || type==='sup_tax_invoice') {
        doc = {
            products,
            deliveryCertificatesIds,
            round,
            round_tax,
            hand,
            hand_tax,
            discount,
            payments,
            taxValue,
            preValue,
            comment,
            details,
            hand_text,
            handArray,
        };
        doc.value_left = header ? header.valueLeft : nkObject?.value_left
    } else if (type === 'receipt' || type==='sup_receipt') {
        doc = {
            receiptsIds,
            receiptsDisplayIds,
            payments,
            comment,
            details,
            refundRest: refundRest
        };
        doc.ref_id = refIdParam ?? (header ? header.refId : nkObject?.ref_id);
    } else if (type === 'order') {
        doc = {
            products,
            taxValue,
            preValue,
            category,
            comment,
            orderStatus,
            staticData,
            delivery_date,
        };
        doc.driver = resolveDocumentDriverName(nkObject, header);
        doc.collector = resolveDocumentCollectorName(nkObject, header);
        doc.ref_id = header ? header.refId : nkObject?.ref_id;
    } else if (type === 'debt') {
        doc = {   comment};
    } else if (type === 'visit_note') {
        const product = header ? header.product : nkObject?.getConnectedProduct()
        const amounts = header ? header.amounts : nkObject?.getAmounts()
        const connectedValue = header ? header.value : nkObject?.getConnectedValue()
        doc = {
            comment,
            payment: header ? header.connectedPayment : nkObject?.getConnectedPayment(),
            product: amounts ? {id:product?.id,name:product?.name,amount:roundDecimals(amounts),price:roundDecimals(connectedValue/amounts),total:roundDecimals(connectedValue)} :undefined,
            products:products

        };
        doc.states = (header ? header.states : nkObject?.getStatesStr()?.toArray()) ?? [];
    }
    doc.id = id;
    doc.entId = entId;
    doc.date = date;
    doc.type = type;
    doc.pdf = isDocumentPrinted({pdf: header?.pdf ?? nkObject?.pdf});
    doc.end = date ? addHours(parseJSON(date), 1) : new Date();
    doc.issueDate = issueDate;
    doc.status = status;
    doc.agent = agent;
    doc.entName = entName;
    doc.entBName = entBName;
    doc.mainValue = mainValue;
    doc.sign = sign;
    doc.photo = photo;
    doc.photoCount = photoCount ?? (photo ? 1 : 0);
    doc.nkObject = nkObject;
    doc.header = header;
    doc.totalAmount = header ? header.totalAmount : nkObject?.getAmounts()
    doc.taxConfirmation=taxConfirmation
    doc.supplierInvoiceNumber = supplierInvoiceNumber
        ?? (typeof nkObject?.supplierInvoiceNumber === 'function' ? nkObject.supplierInvoiceNumber() : '')
    doc.supplierInputEquipment = supplierInputEquipment
        ?? details?.supplierInputEquipment
        ?? (typeof nkObject?.supplierInputEquipment === 'function' ? !!nkObject.supplierInputEquipment() : false)
    doc.driver = resolveDocumentDriverName(nkObject, header);
    doc.collector = resolveDocumentCollectorName(nkObject, header);
    // enrich
    doc.allDay = allDay;
    doc.title = `${documentTypes[type].name} ${displayDocumentId(doc) || id.toString()}`;
    doc.name = documentTypes[type].name;
    doc.description = documentTypes[type].name;
    doc.color = documentTypes[type].color;
    doc.textColor = documentTypes[type].textColor;
    doc.baseColor = documentTypes[type].baseColor;
    doc.notes2=notes2 ?? header?.notes2 ?? nkObject?.notes2
    doc.notes3=notes3 ?? header?.notes3 ?? nkObject?.notes3
    doc.notes4=notes4 ?? header?.notes4 ?? nkObject?.notes4
    doc.notes5=notes5 ?? header?.notes5 ?? nkObject?.notes5
    doc.notes6=notes6 ?? header?.notes6 ?? nkObject?.notes6
    doc.notes7=notes7 ?? header?.notes7 ?? nkObject?.notes7
    doc.notes8=notes8 ?? header?.notes8 ?? nkObject?.notes8
    doc.notes9=notes9 ?? header?.notes9 ?? nkObject?.notes9
    doc.notes10=notes10 ?? header?.notes10 ?? nkObject?.notes10
    doc.agent_id=agent_id ?? (header ? header.agent_id : nkObject?.agent_id)
    doc.orderId = header ? header.order_id : nkObject?.order_id;
    (doc as any).row_version = header?.row_version ?? nkObject?.rowVersion?.() ?? nkObject?.row_version ?? 0;
    {
        const staticFromNk = nkObject?.getStaticData?.();
        doc.staticData = staticData ?? (staticFromNk ? serializeDetails(staticFromNk) : undefined);
    }
    {
        const dd = header ? header.delivery_date : nkObject?.getDeliveryDate?.();
        doc.delivery_date = delivery_date ?? (dd ? StringToDateObject(dd) : undefined);
    }
    doc.history =history;



    doc.notvalid = (doc as ProductNote)?.products?.some((p) => ((p.quantity > 0 || p.quantityReturns > 0) && p.totalPrice == 0)) ?? false;
    // JSON header carries plain {send, reason} objects — same shape consumers read.
    doc.storeNext = header ? header.storeNext : doc?.nkObject?.getStoreNext();
    doc.manager = header ? header.manager : doc?.nkObject?.getManager();
    doc.hashavshevet = header ? header.hashavshevet : doc?.nkObject?.getHashavshevet();
    doc.one = header ? header.one : doc?.nkObject?.getOne();
    doc.sentToExternalSoftware = header ? !!header.sentToExternalSoftware : !!doc?.nkObject?.isSentToExternalSoftware?.();
    doc.externalSoftware = header ? header.externalSoftware : doc?.nkObject?.getExternalSoftwareNote?.();

    return doc;


};


export const deserializeTaxNote = (n: any): ExtendedDocument => {
    const j = documentHeader(n);
    let status: DocumentStatus = statusFromJson(j);

    // Partly closed only when some amount still remains and it differs from the full total.
    // (valueLeft≈0 with status still open used to incorrectly show as "נסגר חלקית")
    if (status == "open" && Math.abs(j.valueLeft) > 0.4 && Math.abs(j.valueLeft - j.totalValue) > 0.4)
        status = "partly_closed"
    const includeTax = j.includeTax;
    let payments = [];
    if (j.payments.length > 0) {
        payments = j.payments.map((p, index) => paymentFromJson(p, index));
    }
    let products: IItem[] = [];

    if (j.hasProducts) {
        // products still flow through the raw Kotlin pipeline (itemsApi)
        const client = j.isClient ? JSDB().getClient(j.entId).first : JSDB().getSupplier(j.entId).first;
        products = itemsApi(client, j.connectedDate, JSDB().TaxNoteAssignRealPrice(n, !j.isClient));
    }
    const hand = j.hand;
    let handValue = 0;

    if (hand != null) {
        handValue = hand.value;
        const handTax = hand.taxPaid;
        if (includeTax === 0) {
            handValue -= handTax;
        }
    }
    let handArray: HandLine[] = undefined
    if (j.freeLines.length > 0) {
        handArray = j.freeLines.map(line => {
            const value = roundDecimals(line.value / (line.amount ?? 1))
            const tax = roundDecimals( (line.taxPaid ?? 0) / (line.amount ?? 1))
            return {
                value: includeTax == 1 ? value : value - tax,
                tax: tax,
                description: line.details,
                amount: line.amount ?? 1,
            }

        });
    }

    const round = j.round;
    let roundValue = 0;
    if (round != null) {
        roundValue = round.value;
        const roundTax = round.taxPaid;
        if (includeTax === 0) {
            roundValue -= roundTax;
        }
    }
    const deliveryIds: number[] = j.deliveryIds;
    let type: DocumentType = j.isClient ? 'invoice' :'sup_invoice';
    if (j.taxNoteTypeName === 'WITH_PAYMENT')
        type = j.isClient ? 'tax_invoice': 'sup_tax_invoice';
    else if (j.taxNoteTypeName === 'CANCEL')
        type = j.isClient? 'refund': 'sup_refund';


    return createDocument(
        {
            mainValue: roundDecimals(j.totalValue, 3),
            preValue: roundDecimals(j.value, 3),
            taxValue: roundDecimals(j.taxPaid, 3),
            comment: j.externalDetails,
            handArray: handArray,
            entName: j.entName,
            entBName: j.entBName,
            id: j.id,
            entId: j.entId.toString(),
            date: StringToDateObjectAddTime(j.connectedDate, j.date),
            issueDate: StringToDateTimeObject(j.date),
            type,
            allDay: false,
            status,
            details: detailsFromJson(j.details),
            payments,
            hand: handValue,
            round: roundValue,
            receiptsIds: [],
            deliveryCertificatesIds: deliveryIds,
            products,
            agent: j.agent,
            discount: j.discountPercent,
            sign: j.sign,
            photo: j.photo,
            photoCount: j.photoCount,
            taxConfirmation: j.taxConfirmation,
            supplierInvoiceNumber: j.supplier_invoice_number
                ?? (typeof n?.supplierInvoiceNumber === 'function' ? n.supplierInvoiceNumber() : ''),
            supplierInputEquipment: !!(j.details?.supplier_input_equipment
                ?? (typeof n?.supplierInputEquipment === 'function' ? n.supplierInputEquipment() : false)),
            nkObject: n,
            header: j
        }
    );
};
export const deserializePayNote = (n: any): ExtendedDocument => {
    const j = documentHeader(n);
    const status: DocumentStatus = statusFromJson(j);
    const date =StringToDateObjectAddTime(j.connectedDate, j.date)
    const payments = j.payments.map((p, index) => paymentFromJson(p, index,date));

    const taxNotes = j.taxNotes;


    return createDocument(
        {
            mainValue: roundDecimals(j.value, 3), preValue: 0, taxValue: 0,
            comment: j.externalDetails,
            entName: j.entName,
            entBName: j.entBName,
            id: j.id,
            entId: j.entId.toString(),
            date: date,
            issueDate: StringToDateTimeObject(j.date),
            type: (j.isClient) ?'receipt':'sup_receipt',
            allDay: false,
            details: detailsFromJson(j.details),
            status,
            payments,
            receiptsIds: taxNotes,
            receiptsDisplayIds: Array.isArray(j.taxNotesDisplay) ? j.taxNotesDisplay.map(String) : undefined,
            deliveryCertificatesIds: [],
            products: [],
            agent: j.agent,
            sign: j.sign,
            photo: j.photo,
            photoCount: j.photoCount,
            nkObject: n,
            header: j
        }
    );
};
export const toArrayOrEmpty = (n: any): [] => {
    return (n.size == 0) ? [] : n.toArray()
};
export const deserializeDebt = (n: any): ExtendedDocument => {
    const j = documentHeader(n);
    const status: DocumentStatus = statusFromJson(j);
    return createDocument(
        {
            comment: j.notes,
            mainValue: roundDecimals(j.value, 3), preValue: 0, taxValue: 0,
            entName: j.entName ?? '',
            entBName: j.entBName ?? '',
            id: j.id,
            entId: j.entId.toString(),
            date: StringToDateObjectAddTime(j.connectedDate, j.date),
            issueDate: StringToDateTimeObject(j.connectedDateTime),
            type: 'debt',
            allDay: false,
            status,

            agent: j.agent,
            nkObject: n,
            header: j
        }
    );
};
export const deserializeVisitNote = (n: any): ExtendedDocument => {
    const j = documentHeader(n);
    let status: DocumentStatus;
    if (j.used) {
        status = 'closed';

    } else if (j.active) {
        status = 'open';
    } else {
        status = 'canceled';
    }
    let products: IItem[] | undefined = undefined;
    if (j.hasConnectedProducts) {
        // products still flow through the raw Kotlin pipeline (itemsApi)
        const client = JSDB().getClient(j.entId).first;
        products = itemsApi(client, j.connectedDate, JSDB().deliveryNoteAssignRealPrice(true, n.getConnectedOrder()));
    }
    return createDocument(
        {
            mainValue: roundDecimals(j.value, 3), preValue: 0, taxValue: 0,
            entName: j.entName,
            entBName: j.entBName,
            id: j.id,
            entId: j.entId.toString(),
            date: StringToDateObjectAddTime(j.connectedDate, j.dateIssued),
            issueDate: StringToDateTimeObject(j.dateIssued),
            type: 'visit_note',
            allDay: false,
            status,
            products: products,
            agent: j.agent,
            comment: j.comment,
            photo: j.photo,
            photoCount: j.photoCount,
            nkObject: n,
            header: j
        }
    );
};
export const deserializeDeliveryNote = (n: any): ExtendedDocument => {
    const j = documentHeader(n);
    const status: DocumentStatus = statusFromJson(j);
    const isClient = j.docType != 6
    // client object is still needed for the raw product pipeline (itemsApi)
    const client = isClient ? JSDB().getClient(j.entIdRaw).first : JSDB().getSupplier(j.entIdRaw).first;
    const isInventory = j.docType === 10 && j.entIdRaw==-1

    let type: DocumentType = 'delivery_certificate';
    let orderStatus = undefined;
    if (j.docType === 10) {
        type = 'order';
        orderStatus = j.orderStatusName;
    } else if (j.docType === 6) {
        type = 'supplier_note'
    }
    let history: IItem[]|undefined = undefined;
    if(j.hasHistory){
        history= itemsApi(client, j.connectedDate, (n.histroy_info))
    }
    const x = createDocument(
        {
            mainValue: roundDecimals(j.value, 3),
            preValue: roundDecimals(j.value - j.taxPaid, 3),
            taxValue: roundDecimals(j.taxPaid, 3),
            id: j.id,
            entId: j.entId.toString(),
            entName: j.entName ?? (isInventory ? 'כניסת מלאי' : ''),
            entBName: j.entBName ?? '',
            date: StringToDateObjectAddTime(j.connectedDate, j.dateIssued),
            issueDate: StringToDateTimeObject(j.dateIssued),
            type,
            allDay: false,
            status,
            comment: j.notes,
            category: j.category,
            bul: Number(j.bul),
            payments: [],
            receiptsIds: [],
            deliveryCertificatesIds: [],
            products: itemsApi(client, j.connectedDate, client ? JSDB().deliveryNoteAssignRealPrice(isClient, n): n.delivery_info),
            history: history,
            agent: j.agent,
            orderStatus: orderStatus,
            sign: j.sign,
            notes2: j.notes2,
            notes3: j.notes3,
            notes4: j.notes4,
            notes5: j.notes5,
            notes6: j.notes6,
            notes7: j.notes7,
            notes8: j.notes8,
            notes9: j.notes9,
            notes10: j.notes10,
            photo: j.photo,
            photoCount: j.photoCount,
            nkObject: n,
            header: j
        }
    );

    return x
};
export const isNoteCompare = (products1:IItem[],products2:IItem[])=>{

    if(products1.length!=products2.length){
        return false
    }
    const pd = {}
    products2.forEach(p=>{
        pd[p.id]=p
    })

    for(let i=0;i<products1.length;i++){

        if(!(products1[i].id in pd) || products1[i].quantity!=pd[products1[i].id].quantity ){

            return false
        }
    }
    return true

}

export const deserializeDocument = (d: any): ExtendedDocument | null => {

    const nkDocType: number = d.getConnectedDocType();
    let doc: ExtendedDocument;

    if (nkDocType === 4 || nkDocType === 10 || nkDocType === 11 || nkDocType === 6) {
        doc = deserializeDeliveryNote(d);
    } else if (nkDocType === 0 || nkDocType === 1 || nkDocType === 2 || nkDocType==16 || nkDocType==17 || nkDocType==18) {
        doc = deserializeTaxNote(d);
    } else if (nkDocType === 7 || nkDocType === 19) {
        doc = deserializePayNote(d);
    } else if (nkDocType === 9 || nkDocType === 20) {
        doc = deserializeDebt(d);
    } else if (nkDocType === 12) {
        doc = deserializeVisitNote(d);
    } else {
        return null;
    }
    return doc;
};


export const deserializeCarteset = (c: ExtendedDocument): CartesetDataHolder => {
    let value_tn = 0;
    let value_pay = 0;
    let yearly = 0
    let value_left =0
    if (c.type === 'receipt' || c.type=='sup_receipt') {
        value_pay = c.mainValue;
    }
    if (c.type === 'tax_invoice' || c.type=='sup_tax_invoice') {
        value_tn = c.mainValue;
        value_pay = c.mainValue;
    }
    if (c.type === 'invoice' || c.type=='sup_invoice') {
        value_tn = c.mainValue;
        value_left = c.value_left??0;
    }
    if (c.type === 'refund' || c.type=='sup_refund') {
        value_tn = -c.mainValue;
        value_left = -c.value_left??0;
    }
    if (c.type === 'debt' || c.type=='sup_debt') {
        value_tn = c.header ? c.header.positive : c.nkObject.positive;
        value_pay = c.header ? c.header.negative : c.nkObject.negative;
        yearly = c.header ? c.header.state : c.nkObject.state
    }
    console.log(c)

    return {
        doc: c,
        id: c.id,
        name: c.name,
        agent: c.agent,
        status: c?.status ?? 'closed',
        date: c.date,
        dateIssue: c.issueDate,
        amount_tn: 0,
        value_tn,
        type:c.type,
        open_tn:value_left ,
        amount_pay: 0,
        value_pay,
        debt: 0,
        cname: c.entName,
        comment: c.comment,
        yearly: yearly
    };

};

export const distinctValuesCreators = (d: any, keys, queryFunc?): string[] => {

    const x = new Set<String>()
    if (d.values == 0)
        return [];

    keys.forEach((k) => {
        const arr: string[] = d[k];
        if (arr)
            arr.forEach((r) => x.add(r.trim()))
    });


    return Array.from(x);
};

/** Closed (used) order that already has a delivery note — date must not move silently. */
export const isClosedOrderWithDeliveryNote = (doc: {
    type?: string;
    status?: string;
    id?: any;
    entId?: any;
    date?: Date;
    orderId?: any;
    nkObject?: any;
    readonly?: boolean;
    dvs?: any[];
} | null | undefined): boolean => {
    if (!doc) return false;
    // Datasheet row: readonly is set together with dvs when a delivery note exists.
    if (doc.readonly && (doc.dvs?.length ?? 0) > 0) return true;

    if (doc.type === 'order') {
        if (doc.status !== 'closed') return false;
        try {
            const dateStr = dateObjectToString(doc.date as Date);
            const notes = JSDB().getClientNotesForCalendar(Number(doc.entId), dateStr);
            const arr = notes?.toArray?.() ?? [];
            if (arr.length === 0) return true;
            return arr.some((n) => String(n.order_id) === String(doc.id));
        } catch {
            return true;
        }
    }

    if (doc.type === 'delivery_certificate') {
        // Only block when this delivery note itself is closed (invoiced) and tied to an order.
        if (doc.status !== 'closed') return false;
        const order = doc.nkObject?.getConnectedOrder?.();
        return !!(doc.orderId || order);
    }

    return false;
};

export const closedOrderWithDeliveryWarning = {
    title: 'הזמנה סגורה עם תעודת משלוח',
    text: 'יש תעודת משלוח על הזמנה זו וההזמנה סגורה.',
};
